import { useState } from "react";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function CustomerSignIn() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user && user.role === "customer") {
    const redirectTo = location.state?.from || "/account";
    return <Navigate to={redirectTo} replace />;
  }
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    const res = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (!res.ok) return toast.error(res.error);
    if (res.user?.role === "admin") {
      toast.success("Signed in as owner");
      navigate("/admin");
    } else {
      toast.success(`Welcome back, ${res.user?.name?.split(" ")[0] || ""}`);
      navigate(location.state?.from || "/account");
    }
  };

  return (
    <div data-testid="customer-signin-page" className="min-h-screen grain-overlay">
      <SiteHeader />
      <main className="max-w-md mx-auto px-6 py-16">
        <p className="label-eyebrow">Welcome back</p>
        <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl tracking-tighter">
          Sign in.
        </h1>
        <p className="mt-3 text-sm text-[#4A4341]">
          Sign in to reorder favorites and track your latest order without a number.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <Label htmlFor="signin-email">Email</Label>
            <Input
              id="signin-email"
              type="email"
              data-testid="signin-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              className="mt-1 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46]"
            />
          </div>
          <div>
            <Label htmlFor="signin-password">Password</Label>
            <Input
              id="signin-password"
              type="password"
              data-testid="signin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="mt-1 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46]"
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            data-testid="signin-submit-btn"
            className="btn-brand w-full rounded-full py-6 text-sm font-semibold inline-flex items-center justify-center gap-2"
          >
            {submitting ? "Signing in…" : "Sign in"}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </form>

        <div className="mt-6 text-right">
          <Link
            to="/forgot-password"
            data-testid="signin-forgot-password-link"
            className="text-xs text-[#7B7573] hover:text-[#C05A46] transition-colors"
          >
            Forgot password?
          </Link>
        </div>

        <div className="mt-8 flex items-center justify-between text-sm">
          <Link to="/register" data-testid="signin-to-register-link" className="text-[#C05A46] hover:underline">
            Create an account
          </Link>
          <Link to="/order" data-testid="signin-guest-link" className="text-[#7B7573] hover:text-[#1A1514] transition-colors">
            Continue as guest →
          </Link>
        </div>
      </main>
    </div>
  );
}
